import { useState } from "react";
import {
  Calendar,
  ChevronDown,
  Mail,
  Phone,
  RotateCw,
  Sparkles,
  X,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { NextActionType } from "../types";

// Sprint 10.x — Spec Théo : "relancer / appeler / envoyer démo / engager"
// a_mailer relabel "Envoyer démo", a_relancer nouveau.
const TYPE_OPTIONS: {
  value: NextActionType;
  label: string;
  Icon: typeof Phone;
}[] = [
  { value: "a_relancer", label: "À relancer", Icon: RotateCw },
  { value: "a_appeler", label: "À appeler", Icon: Phone },
  { value: "a_mailer", label: "Envoyer démo", Icon: Mail },
  { value: "a_engager", label: "À engager", Icon: Sparkles },
  { value: "aucune", label: "Aucune action", Icon: X },
];

const TYPE_LABEL: Record<NextActionType, string> = {
  a_relancer: "À relancer",
  a_appeler: "À appeler",
  a_mailer: "Envoyer démo",
  a_engager: "À engager",
  aucune: "Aucune action prévue",
};

const TYPE_ICON: Record<NextActionType, typeof Phone> = {
  a_relancer: RotateCw,
  a_appeler: Phone,
  a_mailer: Mail,
  a_engager: Sparkles,
  aucune: X,
};

function ymd(d: Date) {
  // YYYY-MM-DD local
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function quickDate(offsetDays: number) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return ymd(d);
}

function formatDateFr(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

type Value = {
  type: NextActionType;
  date: string | null;
};

type Props = {
  value: Value;
  onSave: (next: Value) => void;
};

export function NextActionPopover({ value, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<NextActionType>(value.type);
  const [date, setDate] = useState<string | null>(value.date);

  const isAucune = value.type === "aucune" || !value.type;
  const Ic = isAucune ? Calendar : TYPE_ICON[value.type];

  const reset = () => {
    setType(value.type);
    setDate(value.date);
  };

  const handleSave = () => {
    onSave({ type, date: type === "aucune" ? null : date });
    setOpen(false);
  };

  const handleNoAction = () => {
    setType("aucune");
    setDate(null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border text-[12.5px] transition-colors hover:bg-muted/50",
            isAucune && "text-muted-foreground border-dashed",
          )}
        >
          <Ic className="size-3.5 shrink-0" />
          <div className="flex flex-col items-start leading-tight">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Prochaine action
            </span>
            <span className="font-medium">{TYPE_LABEL[value.type]}</span>
          </div>
          {!isAucune && value.date && (
            <div className="ml-2 pl-2 border-l border-border flex flex-col items-start leading-tight">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Quand
              </span>
              <span className="font-medium tabular-nums">
                {formatDateFr(value.date)}
              </span>
            </div>
          )}
          <ChevronDown className="size-3 text-muted-foreground ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1.5 font-semibold">
            Type d'action
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {TYPE_OPTIONS.map((opt) => {
              const I = opt.Icon;
              return (
                <Button
                  key={opt.value}
                  type="button"
                  variant={type === opt.value ? "default" : "outline"}
                  size="sm"
                  className="h-8 text-[12.5px] justify-start gap-1.5"
                  onClick={() => setType(opt.value)}
                >
                  <I className="size-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>

        {type !== "aucune" && (
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1.5 font-semibold">
              Date
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-1.5">
              {[
                { label: "Aujourd'hui", v: quickDate(0) },
                { label: "Demain", v: quickDate(1) },
                { label: "Dans 3 jours", v: quickDate(3) },
                { label: "Dans 1 semaine", v: quickDate(7) },
              ].map((opt) => (
                <Button
                  key={opt.label}
                  type="button"
                  variant={date === opt.v ? "default" : "outline"}
                  size="sm"
                  className="h-8 text-[12.5px]"
                  onClick={() => setDate(opt.v)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <input
              type="date"
              value={date ?? ""}
              onChange={(e) => setDate(e.target.value || null)}
              className="w-full h-8 px-2 text-[12.5px] rounded-md border border-input bg-background"
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-[12px] text-muted-foreground justify-start px-2"
            onClick={handleNoAction}
          >
            Pas de prochaine action
          </Button>
          <div className="flex gap-1.5 justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 flex-1"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-8 flex-1"
              onClick={handleSave}
            >
              Valider
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
